/* ============================================================
   Fable Motion Layer v14 — The Twelver Studios
   Companion to fable-motion.css. Vanilla JS, no dependencies.
   v14 (2026-09-28, motion draft):
   - One animation system on EVERY page: the theme's AOS entrance
     attributes are stripped site-wide (was homepage only), also
     for reduced-motion visitors.
   - Cards and list items cascade in, delayed by their position in
     their visual row (no delay when they stack on phones).
   - Feature splits and page-intro bands play a sequence: image
     wipe, eyebrow, gold line, heading, text, button.
   - Gold rules under section headings draw in.
   - FAQ answers slide open/closed; arrow rotates.
   - Article pages: gold reading-progress bar.
   v13 notes still apply: product carousel cards are Flickity
   cells and are never animated individually.
   ============================================================ */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  docEl.classList.add('fm-on');

  function onReady(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  function all(root, sel) { return [].slice.call(root.querySelectorAll(sel)); }

  onReady(function () {
    /* ---- One animation system: strip the theme's AOS everywhere ----
       Removing data-aos removes the CSS that hides elements, so
       nothing waits on two stacked animation systems. */
    all(document, '[data-aos]').forEach(function (el) {
      el.removeAttribute('data-aos');
      el.removeAttribute('data-aos-delay');
      el.classList.remove('aos-init', 'aos-animate');
    });

    if (reduced) return;

    var main = document.getElementById('MainContent');
    if (!main) return;
    var vh = window.innerHeight;
    var bodyClass = document.body.className;

    function belowFold(el, f) { return el.getBoundingClientRect().top >= vh * (f || 0.9); }

    var io = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.classList.add('fm-in'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -18% 0px', threshold: 0.05 }) : null; /* v14.1: fire ~10% later (Mohammad, 2026-09-28) */
    function watch(el) { if (io) { io.observe(el); } else { el.classList.add('fm-in'); } }
    function playNow(el) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () { el.classList.add('fm-in'); });
      });
    }

    /* ---- Hero caption scroll depth (unchanged) ---- */
    var hero = main.querySelector('.slideshow-section');
    if (hero && hero.getBoundingClientRect().top + window.pageYOffset < window.innerHeight) {
      hero.classList.add('fm-hero');
      var ticking = false;
      var update = function () {
        ticking = false;
        var y = window.pageYOffset;
        if (y > (hero.offsetHeight || 1)) return;
        hero.style.setProperty('--fm-shift', (y * -0.22).toFixed(1));
      };
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      }, { passive: true });
      update();
    }

    /* ---- Cascades: each item rises, delayed by its column in its row ---- */
    /* onLoad (seconds): items already on screen play on load after that delay instead of staying still
       (the Twelver Designer page, where How it works sits in the first screen under the intro band) */
    function cascade(sel, step, onLoad) {
      var rowTop = null, col = 0;
      all(main, sel).forEach(function (el) {
        var top = Math.round(el.getBoundingClientRect().top);
        if (rowTop === null || Math.abs(top - rowTop) > 4) { rowTop = top; col = 0; } else { col++; }
        if (!belowFold(el)) {
          if (!onLoad) return;
          el.setAttribute('data-fm-reveal', '');
          el.style.setProperty('--fm-delay', (onLoad + Math.min(col, 5) * step).toFixed(2) + 's');
          playNow(el);
          return;
        }
        /* Off-screen sideways = inside a swipe row (e.g. blog picks on phones): leave visible,
           otherwise the card would fade in late while the customer is swiping. */
        var r = el.getBoundingClientRect();
        if (r.left >= window.innerWidth - 1 || r.right <= 1) return;
        el.setAttribute('data-fm-reveal', '');
        el.style.setProperty('--fm-delay', (Math.min(col, 5) * step).toFixed(2) + 's');
        watch(el);
      });
    }
    cascade('.hiw__step', 0.12, 0.45);            /* How it works (plays on load when it starts on screen) */
    cascade('.sbc__item', 0.08);                  /* Shop by category */
    cascade('.bp__item', 0.08);                   /* Blog picks */
    cascade('.blog-card', 0.08);                  /* Blog hub category cards */
    cascade('.bh-post', 0.08);                    /* Blog hub latest articles */
    cascade('.article-index', 0.08);              /* Blog category listing */
    cascade('.accordion__section .details', 0.05);/* FAQ questions */
    cascade('.gallery__section-block', 0.08);     /* Gallery tiles (kept from v13) */

    /* ---- Sequences: feature splits + page-intro bands ---- */
    all(main, '.tf, .tpi').forEach(function (el) {
      el.setAttribute('data-fm-seq', '');
      if (belowFold(el, 0.85)) { watch(el); } else { playNow(el); }
    });

    /* ---- Gold rules under headings draw in ---- */
    all(main, '.hiw__heading, .sbc__heading, .bp__heading, .hiw__num').forEach(function (el) {
      if (!belowFold(el)) return;
      el.setAttribute('data-fm-line', '');
      watch(el);
    });

    /* ---- Whole-section rise for sections that don't animate their own parts ---- */
    var own = '.hiw, .sbc, .tf, .bp, .tpi, .accordion__section, .blog-grid, .blog-template, .article-template, .gallery__section-block, .twd'; /* .twd: the Twelver Designer animates its own parts */
    all(main, ':scope > .shopify-section').forEach(function (s, i) {
      if (i === 0) return;                          /* never the topmost section */
      if (s.querySelector(own)) return;             /* animates its own parts */
      if (!belowFold(s, 0.85)) return;              /* visible on load: no pop */
      s.setAttribute('data-fm-reveal', '');
      watch(s);
    });

    faq(main);
    if (bodyClass.indexOf('template-article') !== -1) readingProgress(main);
  });

  /* ---- FAQ: answers slide open/closed (we manage the one-open-at-a-time group) ---- */
  function faq(main) {
    var list = all(main, 'details.details');
    list.forEach(function (d) {
      var sum = d.querySelector('summary');
      var content = d.querySelector('.details__content');
      if (!sum || !content || !content.animate) return;
      if (d.getAttribute('name')) {                 /* native exclusivity would snap the others shut */
        d.setAttribute('data-fm-name', d.getAttribute('name'));
        d.removeAttribute('name');
      }
      sum.addEventListener('click', function (e) {
        e.preventDefault();
        if (d.getAttribute('data-fm-busy')) return;
        if (d.open) { close(d); return; }
        var group = d.getAttribute('data-fm-name');
        if (group) list.forEach(function (o) { if (o !== d && o.open && o.getAttribute('data-fm-name') === group) close(o); });
        open(d);
      });
    });

    function run(d, frames, done) {
      var c = d.querySelector('.details__content');
      d.setAttribute('data-fm-busy', '1');
      c.style.overflow = 'hidden';
      var a = c.animate(frames, { duration: 320, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' });
      a.onfinish = a.oncancel = function () { c.style.overflow = ''; d.removeAttribute('data-fm-busy'); if (done) done(); };
    }
    function open(d) {
      d.open = true;
      var h = d.querySelector('.details__content').scrollHeight;
      run(d, [{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }]);
    }
    function close(d) {
      var h = d.querySelector('.details__content').getBoundingClientRect().height;
      d.classList.add('fm-closing');
      run(d, [{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], function () {
        d.open = false;
        d.classList.remove('fm-closing');
      });
    }
  }

  /* ---- Article pages: thin gold reading-progress bar ---- */
  function readingProgress(main) {
    var art = main.querySelector('.article-template .rte') || main.querySelector('.article-template');
    if (!art) return;
    var bar = document.createElement('div');
    bar.className = 'fm-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    var ticking = false;
    function update() {
      ticking = false;
      var r = art.getBoundingClientRect();
      var start = r.top + window.pageYOffset;
      var end = start + r.height - window.innerHeight;
      var p = end <= start ? 1 : (window.pageYOffset - start) / (end - start);
      bar.style.transform = 'scaleX(' + Math.min(1, Math.max(0, p)).toFixed(4) + ')';
    }
    function queue() { if (!ticking) { ticking = true; window.requestAnimationFrame(update); } }
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }
})();
